import { useState, useEffect } from "react";
import { Flex, Text } from "rebass";
import styled from "styled-components";
import {
  BiDotsHorizontalRounded,
  BiSolidEditAlt,
  BiMessageDetail,
} from "react-icons/bi";
import { MdHistory, MdReport } from "react-icons/md";
import { BsFillStarFill } from "react-icons/bs";
import { useHistory, useLocation } from "react-router-dom";
import { FiEye } from "react-icons/fi";
import { useSetRecoilState, useRecoilState } from "recoil";
import { SiSteam, SiRiotgames } from "react-icons/si";

import { ReactComponent as HistoryLogo } from "../../../../../../Assets/Nav/nav-history.svg";
import { Button } from "MVVM/Components/Button";
import { DropdownMenu, DropdownMenuItem } from "MVVM/Components/DropdownMenu";
import { useMutateFollowers } from "MVVM/Hooks/useMutateFollowers";
import { usePortal } from "MVVM/Hooks/usePortal";
import { analytics } from "MVVM/Analytics";
import { IUserProfile, ModalType } from "MVVM/@types";
import { useIsFollowingUser } from "MVVM/Hooks/useIsFollowingUser";
import { EditProfileContent } from "../../../EditProfileModal";
import { DirectMessageContent } from "../DirectMessageModal";
import { RenderMatchHistoryWidgetOnProfile } from "MVVM/State/matchHistory";
import { AllstarModalState, ClipFullModalState } from "MVVM/State/modals";
import { extractFileNameFromUrl, isObjectId } from "MVVM/Utilities/Clip";
import { upgradeable, getProfilePlatformType } from "MVVM/Utilities/User";
import { FollowButton } from "MVVM/Components/FollowButton";
import { useUser } from "MVVM/Hooks/useUser";
import { ClipSettingsButton } from "MVVM/Components/ClipSettingsModal/ClipSettingsButton";
import { useClipSettingsModal } from "MVVM/Hooks/useClipQualitySettingsModal";
import {
  addAttributionParams,
  getAttributionUserId,
} from "MVVM/Utilities/Attribution";
import { withReturn } from "MVVM/Utilities/Auth/loginRedirect";

export const ProfileButtons = ({
  user,
  variant,
}: {
  user: IUserProfile;
  variant?: "profileCard" | "default";
}) => {
  const history = useHistory();
  const location = useLocation();
  const { mutateFollowers } = useMutateFollowers();
  const isFollowingUser = useIsFollowingUser(user._id);
  const coverUrl = user.coverImageUrl || "";
  const clipObjectId = extractFileNameFromUrl(coverUrl);
  const { allstarUser } = useUser();
  const isLoggedIn = allstarUser.loggedIn;
  const [copied, setCopied] = useState(false);
  const { open: openClipSettings } = useClipSettingsModal();

  const isValidObjectId = isObjectId(clipObjectId);
  const setClipFullModalState = useSetRecoilState(ClipFullModalState);
  const userGroupType = user.userGroup?.type;

  const isUnclaimedProfile = user.__typename === "UnclaimedProfile";
  const profilePlatformType = getProfilePlatformType(user.playedGames);

  const [
    renderMatchHistoryWidgetOnProfile,
    setRenderMatchHistoryWidgetOnProfile,
  ] = useRecoilState(RenderMatchHistoryWidgetOnProfile);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 3000);
    }
  }, [copied]);

  const showDmButton = !user.isOwnProfile && !isUnclaimedProfile;

  useEffect(() => {
    if (showDmButton) {
      analytics.track("DM Button Viewed", {
        location: "profile",
        source: "header_buttons",
        targetUserId: user._id,
      });
    }
  }, [showDmButton, user._id]);

  const {
    portalOpen: editProfileModalOpen,
    setPortalOpen: setEditProfileModalOpen,
    Portal: EditProfilePortal,
  } = usePortal(
    <EditProfileContent
      onClose={() => setEditProfileModalOpen(false)}
      user={user}
    />,
  );

  const {
    portalOpen: directMessageModalOpen,
    setPortalOpen: setDirectMessageModalOpen,
    Portal: DirectMessagePortal,
  } = usePortal(
    <DirectMessageContent
      onClose={() => setDirectMessageModalOpen(false)}
      user={user}
    />,
  );

  const setAllstarModalState = useSetRecoilState(AllstarModalState);

  const handleFollow = async () => {
    if (!isLoggedIn) {
      setAllstarModalState((prev) => ({
        ...prev,
        isOpen: ModalType.Signup,
        data: {
          target: user.username,
          avatar: user.avatarUrl ?? "",
          action: "follow",
        },
      }));
      return;
    }
    try {
      await mutateFollowers({
        variables: { userToFollow: user._id, follow: true },
      });
      analytics.track("User Followed", {
        location: "profile",
        source: "header_buttons",
      });
    } catch (error) {
      alert("Error following user. Please try again.");
    }
  };

  const handleUnfollow = async () => {
    try {
      await mutateFollowers({
        variables: { userToFollow: user._id, follow: false },
      });
      analytics.track("User Unfollowed", {
        location: "profile",
        source: "header_buttons",
      });
    } catch (error) {
      alert("Error unfollowing user. Please try again.");
    }
  };

  const handleDirectMessage = () => {
    analytics.track("Button - Click", {
      locationIdentifier: "DM fake door",
      location: "user profile",
    });
    setDirectMessageModalOpen(true);
  };

  const handleClaimProfile = () => {
    analytics.track("Claim Profile Clicked", {
      location: "profile",
      platform: profilePlatformType?.toLowerCase(),
    });
    history.push(withReturn("/register"));
  };

  const dropdownItems: DropdownMenuItem[] = [
    {
      id: "match-history",
      label: "Match History",
      icon: <MdHistory />,
      onClick: () => {
        const currentParams = new URLSearchParams(location.search);
        currentParams.set("tab", "match-history");
        history.push({
          pathname: `/u/${user.username || user._id}`,
          search: currentParams.toString(),
        });
      },
    },
    {
      id: "report-user",
      label: "Report User",
      icon: <MdReport />,
      onClick: () => alert("Report user functionality coming soon"),
    },
    ...(isValidObjectId
      ? [
          {
            id: "cover-image",
            label: "Watch Cover Clip",
            icon: <FiEye />,
            onClick: () => {
              analytics.track("Watch Cover Clip", {
                location: "profile",
                source: "header_dropdown",
              });
              setClipFullModalState({
                isOpen: ModalType.Clip,
                shareId: clipObjectId,
              });
            },
          },
        ]
      : []),
    ...(!renderMatchHistoryWidgetOnProfile
      ? [
          {
            id: "show-top-plays",
            label: "Show Top Plays",
            icon: <StyledHistoryLogo />,
            onClick: () => setRenderMatchHistoryWidgetOnProfile(true),
          },
        ]
      : []),
  ];

  return (
    <>
      <ButtonsContainer mt={variant === "profileCard" ? 4 : 3}>
        {variant === "profileCard" ? (
          <>
            {!user.isOwnProfile && !isFollowingUser ? (
              <FollowButton
                isFollowing={isFollowingUser}
                onFollow={handleFollow}
                onUnfollow={handleUnfollow}
              />
            ) : (
              <PrimaryButton
                onClick={() => {
                  history.push(
                    `/u/${user.username || user._id}?tab=match-history`,
                  );
                }}
              >
                <MdHistory size={18} />
                Match History
              </PrimaryButton>
            )}
            <SecondaryButton
              onClick={() => {
                history.push(`/u/${user.username || user._id}`);
              }}
            >
              <FiEye size={18} />
              View Profile
            </SecondaryButton>
          </>
        ) : user.isOwnProfile ? (
          <>
            <PrimaryButton
              onClick={() => {
                setEditProfileModalOpen(true);
              }}
            >
              <BiSolidEditAlt size={18} />
              Edit Profile
            </PrimaryButton>
            <ClipSettingsButton
              onClick={() => {
                analytics.track("Clip Settings - Open Modal", {
                  where: "Profile",
                });
                openClipSettings();
              }}
            />
            {upgradeable(userGroupType) && (
              <SecondaryButton as="a" href="/upgrade" target="_blank">
                <BsFillStarFill size={18} />
                Upgrade
              </SecondaryButton>
            )}
          </>
        ) : isUnclaimedProfile ? (
          <Flex style={{ gap: "8px", alignItems: "center" }}>
            {!isLoggedIn &&
              (profilePlatformType === "Steam" ? (
                <ClaimButtonSteam onClick={handleClaimProfile}>
                  <SiSteam size={18} />
                  Claim With Steam
                </ClaimButtonSteam>
              ) : profilePlatformType === "Riot" ? (
                <ClaimButtonRiot onClick={handleClaimProfile}>
                  <SiRiotgames size={18} />
                  Claim With Riot
                </ClaimButtonRiot>
              ) : null)}
            {!copied ? (
              <Button
                className="roundedFilled"
                size="small"
                onClick={() => {
                  const userId = getAttributionUserId(allstarUser);
                  const baseUrl = window.location.href;
                  const profileLink = addAttributionParams(baseUrl, userId);
                  navigator.clipboard.writeText(profileLink);
                  analytics.track("Button - Click", {
                    location: "claimable_profile",
                    locationIdentifier: "share",
                    source: "website",
                  });
                  setCopied(true);
                }}
                style={{
                  width: "100px",
                  height: "2.25rem",
                  lineHeight: "2.25rem",
                  borderRadius: "99px",
                }}
              >
                Share
              </Button>
            ) : (
              <Text color="envy" variant="text.labelXSmall">
                Link Copied to Clipboard!
              </Text>
            )}
          </Flex>
        ) : (
          !isUnclaimedProfile && (
            <>
              <FollowButton
                isFollowing={isFollowingUser}
                onFollow={handleFollow}
                onUnfollow={handleUnfollow}
              />
              <SecondaryButton onClick={handleDirectMessage}>
                <BiMessageDetail size={18} />
                Message
              </SecondaryButton>
              <DropdownMenu
                trigger={
                  <DotsButton>
                    <BiDotsHorizontalRounded size={18} />
                  </DotsButton>
                }
                items={dropdownItems}
              />
            </>
          )
        )}
      </ButtonsContainer>
      {editProfileModalOpen && EditProfilePortal}
      {directMessageModalOpen && DirectMessagePortal}
    </>
  );
};

const PrimaryButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  height: 36px;
  justify-content: center;
  min-width: 140px;
  opacity: 1;
  padding-left: 12px;
  padding-right: 12px;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.darkerEnvy};
  }
`;

const SecondaryButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy50};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  height: 36px;
  justify-content: center;
  min-width: 140px;
  opacity: 1;
  padding-left: 8px;
  padding-right: 8px;
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.navy};
  }
`;

const ClaimButtonSteam = styled.button`
  align-items: center;
  background: linear-gradient(
      0deg,
      ${({ theme }) => theme.colors.steamBlueAlpha20} 0%,
      ${({ theme }) => theme.colors.steamBlueAlpha20} 100%
    ),
    ${({ theme }) => theme.colors.steamBlueBg};
  border: 2px solid ${({ theme }) => theme.colors.steamBlue};
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  height: 36px;
  justify-content: center;
  padding: 0 16px;
  white-space: nowrap;

  &:hover {
    background: linear-gradient(
        0deg,
        ${({ theme }) => theme.colors.steamBlueAlpha30} 0%,
        ${({ theme }) => theme.colors.steamBlueAlpha30} 100%
      ),
      ${({ theme }) => theme.colors.steamBlueBg};
  }
`;

const ClaimButtonRiot = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.riotRedAlpha20};
  border: 2px solid ${({ theme }) => theme.colors.riotRed};
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  height: 36px;
  justify-content: center;
  padding: 0 16px;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.riotRedAlpha90};
  }
`;

const ButtonsContainer = styled(Flex)`
  gap: 8px;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    flex-direction: column;
    width: 100%;
  }
`;

const DotsButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy50};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  height: 36px;
  justify-content: center;
  opacity: 1;
  padding: 0;
  width: 44px;

  &:hover {
    background: ${({ theme }) => theme.colors.navy};
  }
`;

const StyledHistoryLogo = styled(HistoryLogo)`
  fill: ${({ theme }) => theme.colors.chalk};
  height: 24px;
  width: 24px;
`;
